import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ChevronDown, ChevronRight, Loader2, Sparkles, Bot, AlertTriangle, Target, Shield, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { findingsApi, engagementsApi, aiApi, internalAgentsApi, exploitationApi } from '../lib/api-client';
import { matchExploitationCapability } from '../lib/exploitation-capabilities';
import { getPlainEnglishTitle } from '../lib/plain-english';
import { moduleFriendlyName } from '../lib/module-names';
import { getBusinessImpact } from '../lib/business-impact';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge, SeverityBadge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { LoadingState, ErrorState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';

const EXPLOIT_RISK_BADGE_VARIANT: Record<'low' | 'medium' | 'high', 'success' | 'warning' | 'danger'> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
};

/** Phase 4 — AD/red-team tool moduleIds that carry attack-path-relevant evidence (memberOf,
 * servicePrincipalNames, userAccountControl, shares, ...) — same set lib/attack-path.ts's
 * `isPathRelevant` draws the /attack-paths graph from, kept here as a literal list rather than an
 * import so this page doesn't need a findings-list round trip just to check one finding. */
const ATTACK_PATH_MODULE_IDS = new Set(['kali.ldapdomaindump', 'kali.bloodhound', 'kali.enum4linux', 'kali.kerbrute']);

/** Evidence keys worth surfacing in the "Attack path context" card, in display order — anything
 * else in `evidence` is already shown in the generic Evidence card above, this just pulls out the
 * handful of fields that actually explain WHY a finding matters for lateral movement/privesc. */
const ATTACK_PATH_EVIDENCE_KEYS = [
  'memberOf',
  'servicePrincipalNames',
  'userAccountControl',
  'daysSincePasswordChange',
  'shareName',
  'readable',
  'writable',
  'nullSessionAllowed',
  'guestAccessAllowed',
] as const;

const FALSE_POSITIVE_BADGE_VARIANT: Record<'low' | 'medium' | 'high', 'success' | 'neutral' | 'warning'> = {
  low: 'success',
  medium: 'neutral',
  high: 'warning',
};
const FALSE_POSITIVE_LABEL: Record<'low' | 'medium' | 'high', string> = { low: 'Low', medium: 'Medium', high: 'High' };

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  remediated: 'Fixed',
  regressed: 'Came back',
  false_positive: 'Not a real issue',
};

/** BUI-1017: mirrors apps/worker/src/jobs/confirmation-proof-handler.ts's
 * `isDefaultCredential()` — a default-credential finding is never auto-confirmable (it would mean
 * actually authenticating to a live customer service), so this page never offers a "Confirm
 * finding" button for one, only a "Manual verification required" disabled state. */
const DEFAULT_CREDENTIAL_MODULE_ID_FRAGMENTS = ['network', 'credential'];
function isDefaultCredentialModuleId(moduleId: string): boolean {
  return DEFAULT_CREDENTIAL_MODULE_ID_FRAGMENTS.some((fragment) => moduleId.includes(fragment));
}

/** Minimal shape of `evidence.proofResult`, written by a completed confirmation proof
 * (packages/db/src/repositories/findings.ts's `recordProofResult`). */
interface ProofResult {
  confirmed?: boolean;
}

/** Renders a value that might be a primitive, an array of strings, or a nested object —
 * evidence.cvss/evidence.mitreAttack aren't typed fields anywhere in the schema (see
 * FindingDetailRecord's docstring), so whatever shape a future module writes there must be
 * displayable without assuming a specific structure. */
function EvidenceValue({ value }: { value: unknown }) {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number') return <span>{String(value)}</span>;
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return (
      <div className="flex flex-wrap gap-1">
        {(value as string[]).map((v) => (
          <Badge key={v} variant="neutral">
            {v}
          </Badge>
        ))}
      </div>
    );
  }
  return <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-2 text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

export function FindingDetail() {
  const { id } = useParams<{ id: string }>();
  const findingId = id!;
  const { principal } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canConfirm = principal?.role === undefined || can(principal.role, 'finding:confirm');

  // BUI-1017 "Confirm finding": once triggered, poll every 5s (same refetchInterval pattern as
  // RunDetail.tsx) until the finding's status/evidence actually changes — that's the worker job
  // (apps/worker/src/jobs/confirmation-proof-handler.ts) having finished and written its outcome.
  const [confirming, setConfirming] = useState(false);
  const snapshotRef = useRef<{ status: string; evidenceJson: string } | null>(null);
  const [techDetailsOpen, setTechDetailsOpen] = useState(false);

  const findingQuery = useQuery({
    queryKey: ['finding', findingId],
    queryFn: () => findingsApi.get(findingId),
    refetchInterval: confirming ? 5000 : false,
  });
  const finding = findingQuery.data?.finding;

  const engagementQuery = useQuery({
    queryKey: ['engagement', finding?.engagementId],
    // engagementId is null for agent-sourced findings (BUI-1015/BUI-1016) — `enabled` below keeps
    // this query from firing in that case, but the non-null assertion still needs a guard so this
    // compiles under `engagementId: string | null`.
    queryFn: () => engagementsApi.get(finding!.engagementId!),
    enabled: Boolean(finding?.engagementId),
  });

  useEffect(() => {
    if (!confirming || !finding || !snapshotRef.current) return;
    const evidenceJson = JSON.stringify(finding.evidence ?? {});
    if (finding.status !== snapshotRef.current.status || evidenceJson !== snapshotRef.current.evidenceJson) {
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['finding', findingId] });
    }
  }, [confirming, finding, findingId, queryClient]);

  const confirmMutation = useMutation({
    mutationFn: () => findingsApi.confirm(findingId),
    onSuccess: () => {
      snapshotRef.current = { status: finding?.status ?? 'open', evidenceJson: JSON.stringify(finding?.evidence ?? {}) };
      setConfirming(true);
      toast({ title: 'Confirmation in progress...', description: 'Running a targeted safe probe against this target.', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not start confirmation', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // Phase 4 — on-demand (not auto-fetched: it's a real, audited, live-model call, not a free page
  // load) "what could an attacker do" narrative, surfaced in the "AI Attacker Perspective" card
  // below. `mutate()` is also how the card's retry button re-runs this after a failure.
  const narrativeMutation = useMutation({
    mutationFn: () => findingsApi.exploitabilityNarrative(findingId),
    onError: (err) => toast({ title: 'Could not generate narrative', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // Gates every NEW AI feature added below (remediation guidance, false-positive check) — unlike
  // the pre-existing "AI Attacker Perspective" card above, these hide their trigger entirely when
  // OPENROUTER_API_KEY isn't configured, rather than showing a button that always degrades.
  const aiCapabilitiesQuery = useQuery({ queryKey: ['ai-capabilities'], queryFn: () => aiApi.capabilities() });
  const aiEnabled = aiCapabilitiesQuery.data?.enabled ?? false;

  const remediationGuidanceMutation = useMutation({
    mutationFn: () => findingsApi.remediationGuidance(findingId),
    onError: (err) => toast({ title: 'Could not generate guidance', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const falsePositiveMutation = useMutation({
    mutationFn: () => findingsApi.falsePositiveAssessment(findingId),
    onError: (err) => toast({ title: 'Could not assess false positive', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // Safe Exploitation Proof — unlike every other AI mutation on this page, the server can reject
  // this one with a real 403 (AUTHORIZATION_REQUIRED) when the finding's target has no active
  // authorization/scope match; `mutation.isError` below covers that alongside network failures.
  const exploitationProofMutation = useMutation({
    mutationFn: () => findingsApi.exploitationProof(findingId),
    onError: (err) => toast({ title: 'Could not check exploitability', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // Metasploit auxiliary-module confirmation (CLAUDE.md "Metasploit auxiliary-module exception")
  // — Owner/Admin only, and only for internal findings sourced by a connected internal agent (see
  // FindingRecord.agentId's docstring). See apps/api/src/routes/exploitation.ts.
  const canExploit = principal?.role !== undefined && can(principal.role, 'exploitation:manage');
  const showExploitCard = canExploit && finding?.zone === 'internal' && finding?.agentId != null;

  // Read-only agent list (no AI cost) so the card's initial ready/not-available state doesn't
  // require an audited preview call just to render.
  const exploitAgentsQuery = useQuery({
    queryKey: ['internal-agents'],
    queryFn: () => internalAgentsApi.list(),
    enabled: showExploitCard,
  });
  const exploitAgent = exploitAgentsQuery.data?.agents.find((a) => a.id === finding?.agentId);
  const exploitAgentReady = exploitAgent?.status === 'connected' && exploitAgent?.capabilities?.metasploit === true;

  const previewMutation = useMutation({
    mutationFn: () => exploitationApi.preview(findingId),
    onError: (err) => toast({ title: 'Could not preview exploit module', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const [exploitModalOpen, setExploitModalOpen] = useState(false);
  const [exploitFullName, setExploitFullName] = useState('');
  const [exploitTitle, setExploitTitle] = useState('');
  const [exploitAcceptRisk, setExploitAcceptRisk] = useState(false);
  const [exploitPhrase, setExploitPhrase] = useState('');
  const [exploitAuthorizationId, setExploitAuthorizationId] = useState<string | null>(null);

  const resetExploitForm = () => {
    setExploitFullName('');
    setExploitTitle('');
    setExploitAcceptRisk(false);
    setExploitPhrase('');
  };

  const preview = previewMutation.data;
  const previewReady = preview && preview.module !== null ? preview : null;
  const expectedConfirmationPhrase = previewReady ? `EXPLOIT ${previewReady.agentHostname}` : '';
  const exploitFormComplete =
    exploitFullName.trim().length >= 3 &&
    exploitTitle.trim().length >= 2 &&
    exploitAcceptRisk &&
    previewReady !== null &&
    exploitPhrase === expectedConfirmationPhrase;

  const authorizeMutation = useMutation({
    mutationFn: () => {
      if (!previewReady) throw new Error('No module preview available');
      return exploitationApi.authorize(findingId, {
        authorizedByName: exploitFullName.trim(),
        authorizedByTitle: exploitTitle.trim(),
        confirmationPhrase: exploitPhrase,
        acceptRisk: true,
        agentId: previewReady.agentId,
      });
    },
    onSuccess: (data) => {
      setExploitAuthorizationId(data.authorizationId);
      setExploitModalOpen(false);
      resetExploitForm();
    },
    onError: (err) => toast({ title: 'Could not authorize exploitation', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // 3-second poll while the agent runs the module — same refetchInterval pattern used elsewhere on
  // this page (BUI-1017's confirmation poll, RunDetail.tsx).
  const exploitStatusQuery = useQuery({
    queryKey: ['exploit-status', findingId, exploitAuthorizationId],
    queryFn: () => exploitationApi.status(findingId, exploitAuthorizationId!),
    enabled: exploitAuthorizationId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.authorization.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });
  const exploitAuthorization = exploitStatusQuery.data?.authorization;

  if (findingQuery.isLoading) return <LoadingState />;
  if (findingQuery.isError) return <ErrorState error={findingQuery.error} onRetry={() => findingQuery.refetch()} />;
  if (!finding) return <ErrorState error={new Error('Finding not found')} />;

  const cvss = finding.evidence?.cvss ?? finding.evidence?.cvssScore;
  const mitreAttack = finding.evidence?.mitreAttack ?? finding.evidence?.attackMapping;

  const proofResult = finding.evidence?.proofResult as ProofResult | undefined;
  const eligibleForConfirmation = finding.status === 'open' && finding.confidence < 90;
  const isDefaultCredentialType = isDefaultCredentialModuleId(finding.moduleId);
  const showConfirmButton = canConfirm && eligibleForConfirmation && !isDefaultCredentialType && !confirming;
  const showManualVerification = canConfirm && eligibleForConfirmation && isDefaultCredentialType;
  const showConfirmedBadge = !confirming && proofResult?.confirmed === true;
  const showNotConfirmedBadge = !confirming && proofResult?.confirmed === false && finding.status === 'false_positive';

  // Labels which Internal Testing "Authorized Exploitation" capability this finding maps to — the
  // button itself still only calls the single generic findingsApi.confirm() (see that module's own
  // TODO for real per-capability dispatch); this is discoverability, not a different code path.
  const matchedCapability = matchExploitationCapability(finding.moduleId);

  const plainTitle = getPlainEnglishTitle(finding.title);
  const businessImpact = getBusinessImpact(finding.title, finding.severity);

  // TODO: neither of these has a real backend endpoint yet (no PATCH /v1/findings/:id status
  // route, no "add to report" selection endpoint — Reports.tsx only generates a full report of
  // every finding in an engagement). Showing the buttons now, with an honest "coming soon" toast
  // instead of a fabricated no-op success, so the UI matches what Section 5 of the simplified
  // finding-detail layout calls for without claiming functionality that doesn't exist server-side.
  const handleMarkAsFixed = () => {
    toast({
      title: 'Coming soon',
      description: "Manually marking a finding as fixed isn't available yet — status updates automatically once a confirmation probe runs.",
      variant: 'info',
    });
  };
  const handleAddToReport = () => {
    toast({
      title: 'Coming soon',
      description: "Adding individual findings to a report isn't available yet — Reports currently includes every finding for an engagement.",
      variant: 'info',
    });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Finding" description={moduleFriendlyName(finding.moduleId)} />

      {/* Section 1: What was found */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <CardTitle className="text-xl">{plainTitle ?? finding.title}</CardTitle>
            <SeverityBadge severity={finding.severity} />
          </div>
        </CardHeader>
        <CardContent className="text-sm text-ink">
          <p>{finding.description}</p>
        </CardContent>
      </Card>

      {/* Section 2: Why this matters */}
      <Card>
        <CardHeader>
          <CardTitle>Why this matters</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-ink">
          <p>{businessImpact}</p>
        </CardContent>
      </Card>

      {/* Section 3: AI Attacker Perspective — on-demand narrative, see narrativeMutation above. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-brand" />
            AI Attacker Perspective
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-ink">
          {narrativeMutation.isError ? (
            <div className="space-y-2">
              <p className="text-ink-muted">Could not generate narrative. Try again.</p>
              <Button size="sm" variant="outline" onClick={() => narrativeMutation.mutate()}>
                Try again
              </Button>
            </div>
          ) : !narrativeMutation.data && !narrativeMutation.isPending ? (
            <div className="space-y-2">
              <p className="text-ink-muted">See how an attacker would approach this finding.</p>
              <Button size="sm" variant="outline" onClick={() => narrativeMutation.mutate()}>
                <Sparkles size={14} />
                Generate attacker narrative
              </Button>
            </div>
          ) : narrativeMutation.isPending ? (
            <Button size="sm" variant="outline" disabled>
              <Loader2 size={14} className="animate-spin" />
              Generating...
            </Button>
          ) : !narrativeMutation.data!.available ? (
            <p className="text-ink-muted">AI advisory features are not configured for this workspace.</p>
          ) : narrativeMutation.data!.text === null ? (
            <p className="text-ink-muted">The AI response didn't meet our confidence threshold, so no narrative is shown.</p>
          ) : (
            <div className="space-y-3">
              {/* react-markdown (10.x) renders no wrapping element of its own and has no
                  `className` prop — @tailwindcss/typography's `prose` classes aren't installed
                  here, so styling is applied to the wrapping div via arbitrary-variant
                  child selectors instead. */}
              <div className="space-y-3 text-sm leading-relaxed text-ink-muted [&>h1]:text-base [&>h1]:font-semibold [&>h1]:text-ink [&>h2]:mt-3 [&>h2]:text-sm [&>h2]:font-semibold [&>h2]:text-ink [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-4 [&>strong]:font-semibold [&>strong]:text-ink">
                <ReactMarkdown>{narrativeMutation.data!.text}</ReactMarkdown>
              </div>
              <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Exploitation Proof — on-demand, see exploitationProofMutation above. Requires
          an active authorization record for the finding's target (checked server-side before the
          AI is even called — see apps/api/src/routes/findings.ts); this only ever describes a
          proof for a human tester to review, it never executes anything. */}
      {aiEnabled ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={18} className="text-brand" />
              Exploitation Proof
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {exploitationProofMutation.isError ? (
              <div className="space-y-2">
                <p className="text-ink-muted">{friendlyErrorMessage(exploitationProofMutation.error)}</p>
                <Button size="sm" variant="outline" onClick={() => exploitationProofMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : !exploitationProofMutation.data && !exploitationProofMutation.isPending ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Check if this finding is exploitable using safe, authorized techniques.</p>
                <Button size="sm" variant="outline" onClick={() => exploitationProofMutation.mutate()}>
                  Check exploitability →
                </Button>
              </div>
            ) : exploitationProofMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Checking...
              </Button>
            ) : 'error' in exploitationProofMutation.data! ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Could not generate an exploitation proof.</p>
                <Button size="sm" variant="outline" onClick={() => exploitationProofMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {exploitationProofMutation.data.isPossible ? (
                  <Badge variant={finding.severity === 'critical' || finding.severity === 'high' ? 'danger' : 'warning'}>
                    Exploitable (confidence: {Math.round(exploitationProofMutation.data.confidence * 100)}%)
                  </Badge>
                ) : (
                  <Badge variant="success">Not directly exploitable via safe probes</Badge>
                )}

                {exploitationProofMutation.data.isPossible && exploitationProofMutation.data.steps.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Proof steps</p>
                    <ol className="list-decimal space-y-2 pl-5">
                      {exploitationProofMutation.data.steps.map((step, i) => {
                        const command = step.match(/^run:\s*(.+)$/i);
                        return <li key={i}>{command ? <pre className="mt-1 overflow-x-auto rounded-lg bg-surface-sunken p-2 text-xs">{command[1]}</pre> : step}</li>;
                      })}
                    </ol>
                  </div>
                ) : null}

                {exploitationProofMutation.data.isPossible && exploitationProofMutation.data.expectedOutput ? (
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Expected output</p>
                    <p className="text-ink-muted">{exploitationProofMutation.data.expectedOutput}</p>
                  </div>
                ) : null}

                <div className="flex items-start gap-1.5">
                  <Shield size={14} className="mt-0.5 shrink-0 text-ink-faint" />
                  <p className="text-xs text-ink-muted">{exploitationProofMutation.data.safetyNotes}</p>
                </div>

                <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Section 4b: Attempt Exploit — Metasploit auxiliary-module confirmation via the customer's
          own connected internal agent. Owner/Admin only, internal-agent findings only. Every real
          dispatch runs through the same preflight the rest of the platform uses server-side; this
          card only ever reflects what the server allowed. */}
      {showExploitCard ? (
        <Card className="border-danger/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap size={18} className="text-danger" />
              Attempt Exploit
              <Badge variant="danger">Admin only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {exploitAgentsQuery.isLoading ? (
              <Loader2 size={16} className="animate-spin text-ink-faint" />
            ) : exploitAuthorizationId ? (
              exploitAuthorization?.status === 'completed' ? (
                exploitAuthorization.exploitable === true ? (
                  <div className="space-y-2">
                    <Badge variant="danger">✅ Vulnerability confirmed</Badge>
                    {exploitAuthorization.result.rawOutput ? (
                      <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs">{exploitAuthorization.result.rawOutput}</pre>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Badge variant="success">✓ Not exploitable</Badge>
                    {exploitAuthorization.result.rawOutput ? (
                      <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs">{exploitAuthorization.result.rawOutput}</pre>
                    ) : null}
                  </div>
                )
              ) : exploitAuthorization?.status === 'failed' ? (
                <div className="space-y-2">
                  <Badge variant="warning">⚠ Execution failed</Badge>
                  <p className="text-ink-muted">{exploitAuthorization.result.error ?? 'The agent could not complete this module.'}</p>
                </div>
              ) : (
                <Badge variant="active">
                  <Loader2 size={12} className="mr-1 inline animate-spin" />
                  Running on agent...
                </Badge>
              )
            ) : !exploitAgentReady ? (
              <div className="space-y-2">
                <p className="text-ink-muted">
                  {!exploitAgent
                    ? 'No connected agent found for this finding.'
                    : exploitAgent.status !== 'connected'
                      ? 'The connected agent for this finding is offline.'
                      : 'Metasploit not available on the connected agent. Install Metasploit Framework on the agent host to enable.'}
                </p>
                <Link to="/devices" className="text-xs font-medium text-brand underline-offset-2 hover:underline">
                  Manage agents →
                </Link>
              </div>
            ) : previewMutation.isError ? (
              <div className="space-y-2">
                <p className="text-ink-muted">{friendlyErrorMessage(previewMutation.error)}</p>
                <Button size="sm" variant="outline" onClick={() => previewMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : previewMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Analyzing target...
              </Button>
            ) : !preview ? (
              <div className="space-y-2">
                <p className="text-ink-muted">
                  Run a real, non-destructive Metasploit auxiliary module on the connected agent to confirm this finding is exploitable.
                </p>
                <Button size="sm" variant="danger" onClick={() => previewMutation.mutate()}>
                  <Zap size={14} />
                  Preview module
                </Button>
              </div>
            ) : preview.module === null ? (
              <p className="text-ink-muted">{preview.message}</p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={EXPLOIT_RISK_BADGE_VARIANT[preview.riskLevel]}>{preview.riskLevel} risk</Badge>
                  <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">{preview.module}</code>
                  <span className="text-xs text-ink-faint">confidence: {Math.round(preview.confidence * 100)}%</span>
                </div>
                <p className="text-ink-muted">{preview.explanation}</p>
                {Object.keys(preview.options).length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Options</p>
                    <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-2 text-xs">{JSON.stringify(preview.options, null, 2)}</pre>
                  </div>
                ) : null}
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Expected output</p>
                  <p className="text-ink-muted">{preview.expectedOutput}</p>
                </div>
                <Button size="sm" variant="danger" onClick={() => setExploitModalOpen(true)}>
                  <Zap size={14} />
                  Authorize &amp; run on agent
                </Button>
                <p className="text-xs text-ink-faint">Suggested by DefendIQ AI</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Non-dismissible active-exploitation authorization modal — raw Radix primitives (not the
          shared DialogContent) since this must not close via outside-click, Escape, or a close (X)
          button; only the explicit Cancel button below dismisses it. */}
      {showExploitCard ? (
        <RadixDialog.Root open={exploitModalOpen} onOpenChange={setExploitModalOpen}>
          <RadixDialog.Portal>
            <RadixDialog.Overlay className="fixed inset-0 z-40 bg-overlay" />
            <RadixDialog.Content
              onInteractOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
              className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-danger/40 bg-surface-raised p-6 shadow-lg focus:outline-none"
            >
              <div className="mb-4 flex items-center gap-2">
                <AlertTriangle size={20} className="text-danger" />
                <RadixDialog.Title className="text-lg font-semibold text-ink">Active Exploitation Authorization</RadixDialog.Title>
              </div>
              <RadixDialog.Description className="mb-4 text-sm text-ink-muted">
                This will run a real Metasploit auxiliary module ({previewReady?.module}) against {finding.target} via the connected agent (
                {previewReady?.agentHostname}). This is non-destructive but makes live contact with the target.
              </RadixDialog.Description>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="exploit-full-name">Full name</Label>
                  <Input
                    id="exploit-full-name"
                    className="mt-1"
                    value={exploitFullName}
                    onChange={(e) => setExploitFullName(e.target.value)}
                    disabled={authorizeMutation.isPending}
                  />
                </div>
                <div>
                  <Label htmlFor="exploit-title">Title</Label>
                  <Input
                    id="exploit-title"
                    className="mt-1"
                    value={exploitTitle}
                    onChange={(e) => setExploitTitle(e.target.value)}
                    disabled={authorizeMutation.isPending}
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-ink-muted">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={exploitAcceptRisk}
                    onChange={(e) => setExploitAcceptRisk(e.target.checked)}
                    disabled={authorizeMutation.isPending}
                  />
                  I confirm I am authorized to test this target and accept responsibility for this action.
                </label>
                <div>
                  <Label htmlFor="exploit-phrase">
                    Type <code className="rounded bg-surface-sunken px-1">{expectedConfirmationPhrase}</code> to confirm
                  </Label>
                  <Input
                    id="exploit-phrase"
                    className="mt-1 font-mono"
                    value={exploitPhrase}
                    onChange={(e) => setExploitPhrase(e.target.value)}
                    disabled={authorizeMutation.isPending}
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setExploitModalOpen(false);
                    resetExploitForm();
                  }}
                  disabled={authorizeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button variant="danger" disabled={!exploitFormComplete || authorizeMutation.isPending} onClick={() => authorizeMutation.mutate()}>
                  {authorizeMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                  Run on agent
                </Button>
              </div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      ) : null}

      {/* Section 5: How to fix it */}
      <Card>
        <CardHeader>
          <CardTitle>How to fix it</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-ink">
          {finding.remediation ? (
            <ol className="list-decimal space-y-1 pl-5">
              <li>{finding.remediation}</li>
            </ol>
          ) : (
            <p className="text-ink-muted">No specific remediation guidance is available for this finding type yet.</p>
          )}

          {aiEnabled ? (
            <div className="mt-4 border-t border-border pt-4">
              {!remediationGuidanceMutation.data && !remediationGuidanceMutation.isPending && !remediationGuidanceMutation.isError ? (
                <Button size="sm" variant="outline" onClick={() => remediationGuidanceMutation.mutate()}>
                  <Sparkles size={14} />
                  Get AI remediation steps
                </Button>
              ) : remediationGuidanceMutation.isPending ? (
                <Button size="sm" variant="outline" disabled>
                  <Loader2 size={14} className="animate-spin" />
                  Generating...
                </Button>
              ) : remediationGuidanceMutation.isError || (remediationGuidanceMutation.data && 'error' in remediationGuidanceMutation.data) ? (
                <div className="space-y-2">
                  <p className="text-ink-muted">Could not generate guidance.</p>
                  <Button size="sm" variant="outline" onClick={() => remediationGuidanceMutation.mutate()}>
                    Try again
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <ol className="list-decimal space-y-1 pl-5">
                    {remediationGuidanceMutation.data!.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                  <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Section 6: Technical details (collapsed by default) */}
      <Card>
        <button
          type="button"
          onClick={() => setTechDetailsOpen((v) => !v)}
          aria-expanded={techDetailsOpen}
          className="flex w-full items-center justify-between gap-2 p-6 text-left"
        >
          <span className="text-base font-semibold text-ink">Technical details</span>
          {techDetailsOpen ? <ChevronDown size={18} className="text-ink-faint" /> : <ChevronRight size={18} className="text-ink-faint" />}
        </button>
        {techDetailsOpen ? (
          <CardContent className="space-y-4 pt-0 text-sm">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-ink-faint">Technical title</p>
              <p className="text-ink">{finding.title}</p>
            </div>

            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              <p>
                <span className="text-ink-muted">Target:</span> {finding.target}
              </p>
              <p>
                <span className="text-ink-muted">Zone:</span> {finding.zone}
              </p>
              <p>
                <span className="text-ink-muted">Engagement:</span> {engagementQuery.data?.engagement.name ?? finding.engagementId}
              </p>
              <p>
                <span className="text-ink-muted">Status:</span> <Badge variant="neutral">{STATUS_LABEL[finding.status] ?? finding.status}</Badge>
              </p>
              <p>
                <span className="text-ink-muted">Confidence:</span> {finding.confidence}/100
              </p>
              <p>
                <span className="text-ink-muted">Discovered:</span> {new Date(finding.discoveredAt).toLocaleString()}
              </p>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Evidence</p>
              {Object.keys(finding.evidence ?? {}).length === 0 ? (
                <p className="text-ink-muted">No evidence recorded.</p>
              ) : (
                <pre className="overflow-x-auto rounded-lg bg-surface-sunken p-3 text-xs">{JSON.stringify(finding.evidence, null, 2)}</pre>
              )}
            </div>

            {cvss !== undefined ? (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">CVSS score</p>
                <EvidenceValue value={cvss} />
              </div>
            ) : null}

            {mitreAttack !== undefined ? (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">MITRE ATT&amp;CK</p>
                <EvidenceValue value={mitreAttack} />
              </div>
            ) : null}

            {ATTACK_PATH_MODULE_IDS.has(finding.moduleId) ? (
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-ink-faint">Attack path context</p>
                <p className="text-ink-muted">
                  This finding is part of this environment's Active Directory attack surface.{' '}
                  <Link to="/attack-paths" className="font-medium text-brand underline-offset-2 hover:underline">
                    View in Attack Paths →
                  </Link>
                </p>
                {ATTACK_PATH_EVIDENCE_KEYS.filter((key) => finding.evidence?.[key] !== undefined).map((key) => (
                  <div key={key}>
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-faint">{key}</p>
                    <EvidenceValue value={finding.evidence[key]} />
                  </div>
                ))}
              </div>
            ) : null}

            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">Reproduction steps</p>
              <p className="text-ink-muted">No reproduction steps recorded for this finding.</p>
            </div>
          </CardContent>
        ) : null}
      </Card>

      {/* Section 7: Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          {canConfirm && (eligibleForConfirmation || confirming || showConfirmedBadge || showNotConfirmedBadge) ? (
            <>
              {confirming ? (
                <Badge variant="neutral">Confirmation in progress...</Badge>
              ) : showConfirmedBadge ? (
                <Badge variant="success">Confirmed</Badge>
              ) : showNotConfirmedBadge ? (
                <Badge variant="danger">Could not confirm — possible false positive</Badge>
              ) : showManualVerification ? (
                <span title="Manual verification required — default-credential findings can't be safely auto-confirmed without attempting a live login.">
                  <Button size="sm" variant="primary" disabled>
                    Confirm this is real
                  </Button>
                </span>
              ) : showConfirmButton ? (
                <ConfirmDialog
                  trigger={
                    <Button size="sm" variant="primary">
                      Confirm this is real
                    </Button>
                  }
                  title="Confirm this finding?"
                  description={`This will run a targeted safe probe against ${finding.target} to confirm this finding is real. Proceed?`}
                  confirmLabel="Confirm this is real"
                  onConfirm={() => confirmMutation.mutateAsync()}
                />
              ) : null}
              {(showConfirmButton || showManualVerification) && matchedCapability ? (
                <span className="text-xs text-ink-faint">Runs: {matchedCapability.name}</span>
              ) : null}
            </>
          ) : null}

          {aiEnabled ? (
            !falsePositiveMutation.data && !falsePositiveMutation.isPending && !falsePositiveMutation.isError ? (
              <Button size="sm" variant="outline" onClick={() => falsePositiveMutation.mutate()}>
                <Bot size={14} />
                AI: Check false positive
              </Button>
            ) : falsePositiveMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Checking...
              </Button>
            ) : falsePositiveMutation.isError || (falsePositiveMutation.data && 'error' in falsePositiveMutation.data) ? (
              <div className="flex items-center gap-2">
                <p className="text-xs text-ink-muted">Could not assess false positive.</p>
                <Button size="sm" variant="outline" onClick={() => falsePositiveMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="flex max-w-sm flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Badge variant={FALSE_POSITIVE_BADGE_VARIANT[falsePositiveMutation.data!.likelihood]}>
                    False positive likelihood: {FALSE_POSITIVE_LABEL[falsePositiveMutation.data!.likelihood]}
                  </Badge>
                  {falsePositiveMutation.data!.likelihood === 'high' ? <AlertTriangle size={14} className="text-warning" /> : null}
                </div>
                <p className="text-xs text-ink-muted">{falsePositiveMutation.data!.rationale}</p>
                <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
              </div>
            )
          ) : null}

          <Button size="sm" variant="outline" onClick={handleMarkAsFixed}>
            Mark as fixed
          </Button>
          <Button size="sm" variant="outline" onClick={handleAddToReport}>
            Add to report
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
